/**
 * Background Control Group Constants
 *
 * Default options, configurations and constants for the BackgroundControl component
 *
 * @package RedfoxThankYou
 * @since 1.0.0
 */

import { __ } from '@wordpress/i18n';

import { BREAKPOINT_LABELS } from '../common';
import { BackgroundEnabledControls, PatternOption } from './types';

/**
 * Re-export common constants for backward compatibility
 */
export const breakpointLabels = BREAKPOINT_LABELS;

/**
 * Default enabled controls - only basic controls enabled in free version
 * Premium features (image, gradient, pattern, overlay) are available via Pro plugin
 */
export const DEFAULT_ENABLED_CONTROLS: BackgroundEnabledControls = {
	backgroundColor: true,
	backgroundImage: false, // Premium feature
	backgroundGradient: false, // Premium feature
	backgroundPattern: false, // Premium feature
	backgroundOverlay: false, // Premium feature
	advancedOptions: false, // Premium feature
	responsive: true,
};

/**
 * Background size options
 */
export const backgroundSizeOptions = [
	{ label: __( 'Auto', 'redfox-thank-you' ), value: 'auto' },
	{ label: __( 'Cover', 'redfox-thank-you' ), value: 'cover' },
	{ label: __( 'Contain', 'redfox-thank-you' ), value: 'contain' },
	{ label: __( '100% Width', 'redfox-thank-you' ), value: '100% auto' },
	{ label: __( '100% Height', 'redfox-thank-you' ), value: 'auto 100%' },
	{ label: __( 'Custom', 'redfox-thank-you' ), value: 'custom' },
];

/**
 * Background position options
 */
export const backgroundPositionOptions = [
	{ label: __( 'Top Left', 'redfox-thank-you' ), value: 'top left' },
	{ label: __( 'Top Center', 'redfox-thank-you' ), value: 'top center' },
	{ label: __( 'Top Right', 'redfox-thank-you' ), value: 'top right' },
	{ label: __( 'Center Left', 'redfox-thank-you' ), value: 'center left' },
	{ label: __( 'Center', 'redfox-thank-you' ), value: 'center' },
	{ label: __( 'Center Right', 'redfox-thank-you' ), value: 'center right' },
	{ label: __( 'Bottom Left', 'redfox-thank-you' ), value: 'bottom left' },
	{
		label: __( 'Bottom Center', 'redfox-thank-you' ),
		value: 'bottom center',
	},
	{ label: __( 'Bottom Right', 'redfox-thank-you' ), value: 'bottom right' },
];

/**
 * Background repeat options
 */
export const backgroundRepeatOptions = [
	{ label: __( 'No Repeat', 'redfox-thank-you' ), value: 'no-repeat' },
	{ label: __( 'Repeat', 'redfox-thank-you' ), value: 'repeat' },
	{ label: __( 'Repeat X', 'redfox-thank-you' ), value: 'repeat-x' },
	{ label: __( 'Repeat Y', 'redfox-thank-you' ), value: 'repeat-y' },
	{ label: __( 'Space', 'redfox-thank-you' ), value: 'space' },
	{ label: __( 'Round', 'redfox-thank-you' ), value: 'round' },
];

/**
 * Background attachment options
 */
export const backgroundAttachmentOptions = [
	{ label: __( 'Scroll', 'redfox-thank-you' ), value: 'scroll' },
	{ label: __( 'Fixed', 'redfox-thank-you' ), value: 'fixed' },
	{ label: __( 'Local', 'redfox-thank-you' ), value: 'local' },
];

/**
 * Background pattern options
 */
export const patternOptions: PatternOption[] = [
	{ label: __( 'None', 'redfox-thank-you' ), value: '' },
	{
		label: __( 'Dots', 'redfox-thank-you' ),
		value: 'dots',
		preview: 'radial-gradient(circle, #000 1px, transparent 1px)',
	},
	{
		label: __( 'Lines', 'redfox-thank-you' ),
		value: 'lines',
		preview:
			'repeating-linear-gradient(0deg, transparent, transparent 2px, #000 2px, #000 4px)',
	},
	{
		label: __( 'Grid', 'redfox-thank-you' ),
		value: 'grid',
		preview:
			'linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)',
	},
	{
		label: __( 'Diagonal', 'redfox-thank-you' ),
		value: 'diagonal',
		preview:
			'repeating-linear-gradient(45deg, transparent, transparent 10px, #000 10px, #000 20px)',
	},
	{
		label: __( 'Checkerboard', 'redfox-thank-you' ),
		value: 'checkerboard',
		preview:
			'conic-gradient(#000 90deg, transparent 90deg 180deg, #000 180deg 270deg, transparent 270deg)',
	},
];

/**
 * Blend mode options for overlay
 */
export const blendModeOptions = [
	{ label: __( 'Normal', 'redfox-thank-you' ), value: 'normal' },
	{ label: __( 'Multiply', 'redfox-thank-you' ), value: 'multiply' },
	{ label: __( 'Screen', 'redfox-thank-you' ), value: 'screen' },
	{ label: __( 'Overlay', 'redfox-thank-you' ), value: 'overlay' },
	{ label: __( 'Soft Light', 'redfox-thank-you' ), value: 'soft-light' },
	{ label: __( 'Hard Light', 'redfox-thank-you' ), value: 'hard-light' },
	{ label: __( 'Color Dodge', 'redfox-thank-you' ), value: 'color-dodge' },
	{ label: __( 'Color Burn', 'redfox-thank-you' ), value: 'color-burn' },
	{ label: __( 'Darken', 'redfox-thank-you' ), value: 'darken' },
	{ label: __( 'Lighten', 'redfox-thank-you' ), value: 'lighten' },
	{ label: __( 'Difference', 'redfox-thank-you' ), value: 'difference' },
	{ label: __( 'Exclusion', 'redfox-thank-you' ), value: 'exclusion' },
];

/**
 * Default background attributes for reset functionality
 */
export const DEFAULT_BACKGROUND_ATTRIBUTES = {
	backgroundColor: undefined,
	backgroundImage: undefined,
	backgroundSize: 'cover',
	backgroundPosition: 'center',
	backgroundRepeat: 'no-repeat',
	backgroundAttachment: 'scroll',
	backgroundGradient: undefined,
	customGradient: undefined,
	backgroundPattern: undefined,
	patternOpacity: 1,
	backgroundOverlay: {
		enabled: false,
		color: undefined,
		gradient: undefined,
		opacity: 0.5,
		blendMode: 'normal',
	},
};

/**
 * Default background values for reset
 */
export const defaultBackgroundValues = {
	backgroundColor: undefined,
	backgroundImage: undefined,
	backgroundSize: undefined,
	backgroundPosition: undefined,
	backgroundRepeat: undefined,
	backgroundAttachment: undefined,
	backgroundGradient: undefined,
	customGradient: undefined,
	backgroundPattern: undefined,
	patternOpacity: undefined,
	backgroundOverlay: undefined,
};
